/**
 * Listing query state (spec R20, R21, R23; server-driven since 2026-08-19 —
 * operator decision: search, sort and pagination are ALWAYS API calls).
 *
 * This hook owns what the user chose — search text, Show count, page, sort —
 * and derives the one `CardListQuery` the provider sends to the repository.
 * Nothing here filters, sorts or slices rows: that is the adapter's work.
 *
 * The search text updates per keystroke (the input needs it), but the query
 * carries a DEBOUNCED copy so as-you-type coalesces into one request. A new
 * search, Show count or sort returns to page 1 — the old page position is
 * meaningless against a different result set.
 */

import { useEffect, useMemo, useState } from 'react';

import { TableSortState } from 'types/DesignSystem/Table';

import { DEFAULT_SHOW_COUNT, LISTING_SEARCH_DEBOUNCE_MS } from '../constants';
import { CardListQuery, CardListSortBy, CardListSortDir } from '../types';

/** The listing's default order: newest change on top, like the API's own. */
const DEFAULT_SORT: TableSortState = { columnId: 'updatedAt', direction: 'desc' };

interface UseCardListingResult {
  searchTerm: string;
  /** Also returns to page 1 — a new search invalidates the old page position. */
  setSearchTerm: (searchTerm: string) => void;
  showCount: number;
  /** Also returns to page 1 — a new page size invalidates the old boundaries. */
  setShowCount: (showCount: number) => void;
  /** 1-based; the provider clamps it when the server's total shrinks under it. */
  page: number;
  setPage: (page: number) => void;
  sortState: TableSortState;
  /** Also returns to page 1 — re-sorting reorders every page's contents. */
  setSort: (sortState: TableSortState) => void;
  /** The request all of the above adds up to; referentially stable per content. */
  query: CardListQuery;
  /**
   * The typed term has outrun the one in `query` — i.e. the debounce is still
   * running and the rows on screen answer an older term. Derived, never stored:
   * the listing's `isLoading` starts only when the request does, which is
   * `LISTING_SEARCH_DEBOUNCE_MS` too late to be the whole wait (§ 288).
   */
  isSearchPending: boolean;
}

const toSortQuery = (
  sortState: TableSortState
): { sortBy: CardListSortBy; sortDir: CardListSortDir } => {
  if (sortState.direction === null) {
    return { sortBy: 'updatedAt', sortDir: 'desc' };
  }
  if (sortState.columnId === 'name' || sortState.columnId === 'createdAt') {
    return { sortBy: sortState.columnId, sortDir: sortState.direction };
  }
  return { sortBy: 'updatedAt', sortDir: sortState.direction };
};

export const useCardListing = (): UseCardListingResult => {
  const [searchTerm, setSearchTermState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCount, setShowCountState] = useState<number>(DEFAULT_SHOW_COUNT);
  const [page, setPage] = useState(1);
  const [sortState, setSortState] = useState<TableSortState>(DEFAULT_SORT);

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      LISTING_SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const setSearchTerm = (nextTerm: string) => {
    setPage(1);
    setSearchTermState(nextTerm);
  };

  const setShowCount = (nextCount: number) => {
    setPage(1);
    setShowCountState(nextCount);
  };

  const setSort = (nextSort: TableSortState) => {
    setPage(1);
    setSortState(nextSort);
  };

  const query = useMemo<CardListQuery>(() => {
    const { sortBy, sortDir } = toSortQuery(sortState);
    return { page, limit: showCount, search: debouncedSearch, sortBy, sortDir };
  }, [page, showCount, debouncedSearch, sortState]);

  return {
    searchTerm,
    isSearchPending: searchTerm.trim() !== debouncedSearch,
    setSearchTerm,
    showCount,
    setShowCount,
    page,
    setPage,
    sortState,
    setSort,
    query,
  };
};
